import React, {
  useCallback,
  useRef,
  useMemo,
  forwardRef,
  useState,
  useEffect,
} from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  getGridData,
  getGridFilterValues,
} from "../../services/dataGridService";
import SFCellTextRenderer from "./renderers/cellTextRenderer";
import SFLoadingRenderer from "./renderers/loadingRenderer";
import { nanoid } from "@reduxjs/toolkit";
import { CELL_RENDERER, HEADER_RENDERED } from "./dataGridConfig";
import _ from "lodash";
import SFDataGridCore from "./dataGridCore";
import DataGridSearch from "./dataGridSearch";
import GridFilterChips from "./gridFilterChips";

const SFDataGrid = forwardRef((props, ref) => {
  const IS_CLIENT_GRID = props.rowModelType === "clientSide";
  const gridRef = ref ?? useRef();
  const [rowData, setRowData] = useState(null);
  const savedLayout = useSelector((state) => {
    return state.gridLayout.find(
      (gridLayout) => gridLayout.gridName === props.gridName
    );
  });

  useEffect(() => {
    if (gridRef?.current?.api) {
      if (savedLayout?.state?.filter) {
        changeFilterModel(savedLayout.state.filter.filterModel);
      }
    }
  }, [JSON.stringify(savedLayout?.state)]);

  const getValues = useCallback((params) => {
    let filterModel = _.cloneDeep(params.api.getFilterModel());
    //ignore search text filter
    if (filterModel["searchText"]) {
      delete filterModel["searchText"];
    }
    // The end result of the following code is that it removes properties from the 'filterModel' object
    // if the corresponding column definition object has its 'ignoreFilterOnGetValues' property set to true.
    props.columnDefs
      .filter(
        (columnDef) =>
          columnDef.ignoreFilterOnGetValues && columnDef.field in filterModel
      )
      .forEach((columnDef) => delete filterModel[columnDef.field]);
    getGridFilterValues(
      props.filterValuesUrl,
      JSON.stringify({
        colName: params.colDef.field,
        filterModel: filterModel,
      })
    )
      .then((resp) => resp.data.values)
      .then((filterValues) => {
        params.success(filterValues);
      });
  }, []);

  const handleSearchChange = (val) => {
    if (val !== null) {
       
      let filterModel = {
        searchText: {
          filterType: "textSearch",
          filter: getSearchTextFilterObject(val),
          type: "contains",
        },
      };
      changeFilterModel(filterModel);
    }
  };

  const getSearchTextFilterObject = (val) => {
    return val === ""
      ? ""
      : JSON.stringify({
        value: val,
        excludedProperties: props.excludedSearchProperties,
      });
  };

  const buildColumns = (columnDefs, type = "master") => {
    columnDefs = columnDefs.map((colDef) => ({
      ...colDef,
      //DEFAULT CELL RENDERER - SFCellTextRenderer
      cellRenderer: colDef.cellRenderer ?? CELL_RENDERER.TEXT,
      filter: colDef.filter,
      headerComponent: colDef.tooltip
        ? HEADER_RENDERED.DEFAULT
        : colDef.customHeader
          ? colDef.customHeader
          : "agColumnHeader",
    }));
    //Search box filtering hidden column
    if (type === "master") {
      columnDefs.push({
        field: "searchText",
        hide: true,
        suppressFiltersToolPanel: true,
        suppressColumnsToolPanel: true,
        showChips: false,
        filter: "agTextColumnFilter",
        filterParams: {
          debounceMs: 0,
        },
      });
    }

    columnDefs.push({
      field: "advancedQuery",
      hide: true,
      suppressFiltersToolPanel: true,
      suppressColumnsToolPanel: true,
      showChips: false,
      filter: "agTextColumnFilter",
    });
    return columnDefs;
  };

  //CREATING COLUMN DEFINITIONS BASED ON PROPS
  const colDefs = useMemo(() => buildColumns(props.columnDefs), []);

  const getServerSideDataSource = () => {
    let request = createRequest(props.datasourceUrl, setRowData);
    return createServerSideDatasource(request);
  };

  const getRowId = (params) => {
    // use nanoid in case row hasn't id (eg. row group)
    return params.data?.id ?? nanoid();
  };

  const changeFilterModel = (filterModel) => {
    let exFilterModel = _.cloneDeep(gridRef.current.api.getFilterModel());
    let newFilterModel = Object.assign({}, exFilterModel, filterModel);
    if (_.isEmpty(filterModel)) {
      gridRef.current.api.setFilterModel({});
    } else {
      gridRef.current.api.setFilterModel(newFilterModel);
    }
  };

  // Function that runs when the filters are initialized to get the values for the filters
  const initializeFilter = () => {
    // Find the column that has the selectedFilterColumn property set to true
    let filterColumn = props.columnDefs.find((col) => col.selectedFilterColumn);
    let filterInstance = null;
    // Get the filter instance for the column that automatically runs the getValues function
    gridRef.current.api
      .getColumnFilterInstance(filterColumn.field)
      .then((filter) => {
        filterInstance = filter;
      });

    // Wait for the function getValues to return the values for the filter before resolving the promise
    return new Promise((resolve, reject) => {
      let halfSecondsElapsed = 0;

      // Check every half second if the filter values have been returned for 5 seconds
      const intervalId = setInterval(() => {
        const values = filterInstance?.getFilterValues();
        if (values && Array.isArray(values) && values.length > 0) {
          clearInterval(intervalId);
          resolve(filterInstance);
        } else if (halfSecondsElapsed >= 10) {
          clearInterval(intervalId);
          reject();
        }
        halfSecondsElapsed += 1;
      }, 500);
    });
  };

  // Function that runs when the filters are changed to set the filter model
  const handleFilterChange = (values) => {
    // Find the column that has the selectedFilterColumn property set to true
    let filterColumn = props.columnDefs.find((col) => col.selectedFilterColumn);
    // Set the filter model for the column and set it
    let filterModel = {
      [filterColumn.field]: { filterType: "set", values: values },
    };
    changeFilterModel(filterModel);
  };

  return (
    <React.Fragment>
      {props.hasFilterChips && (
        <GridFilterChips
          changeFilterModel={changeFilterModel}
          gridName={props.gridName}
          getSearchChipDetail={props.getSearchChipDetail}
          gridRef={gridRef}
        />
      )}
      <SFDataGridCore
        {...(IS_CLIENT_GRID && {
          gridTheme: "ag-theme-ey-client",
        })}
        getRowId={getRowId}
        customRowData={rowData}
        {...props}
        search={
          <DataGridSearch
            onChange={(val) => handleSearchChange(val)}
            selectedFilterColumn={
              props.columnDefs.find((x) => x.selectedFilterColumn)?.field
            }
            gridName={props.gridName}
            initializeFilter={initializeFilter}
            handleFilterChange={handleFilterChange}
            searchLabel={props.searchLabel}
            filterLabel={props.filterLabel}
          />
        }
        //debug={true}
        //This stalls any request done from the grid for 1 second its usefull when user scrolls fast to block any unnecessary requests
        //and also usefull when setting filter/sort model on grid ready to avoid any extra requests
        //blockLoadDebounceMillis={1000}
        ref={gridRef}
        persistState={props.persistState}
        //enables master detail
        // detailRowHeight={props.detailHeight ?? 450}
        // detailCellRenderer={"SFCustomDetailRenderer"}
        // rowBuffer={0}
        rowModelType={props.rowModelType || "serverSide"}
        //decides whether to load all rows or load each block on scroll
        suppressServerSideInfiniteScroll={props.loadAllRows ? true : false}
        //number of rows to load per request
        {...(!IS_CLIENT_GRID && { cacheBlockSize: 50 })}
        //number of rows to save in cache  5 * 20 = 100 rows, when requests exceed that number memory is cleared
        {...(!IS_CLIENT_GRID && { maxBlocksInCache: 2 })}
        //custom renderers. could have an impact on grid lag. Need to test in production as indicated by ag grid documentation
        customRenderers={{
          ...props.customComponents,
          SFCellTextRenderer: SFCellTextRenderer,
          agLoadingCellRenderer: SFLoadingRenderer,
        }}
        columnDefs={colDefs}
        {...(!IS_CLIENT_GRID && {
          getValues: getValues,
          getServerSideDataSource: getServerSideDataSource,
        })}
        gridName={props.gridName}
        filterValuesUrl={props.filterValuesUrl}
        isClientGrid={IS_CLIENT_GRID}
      />
    </React.Fragment>
  );
});

function createServerSideDatasource(server) {
  return {
    getRows: async function (params) {
      let response = await server.getData(params.request);
      if (response.success) {
        params.success({
          rowData: response.rowData,
        });
      } else {
        params.fail();
      }
    },
  };
}

function createRequest(url, setRowData) {
  return {
    getData: async (request) => {
      let response = await getGridData(url, JSON.stringify(request));
      let result = response.data;
      //set timeout here added as suggested by ag grid to resolve the redraw rows bug
      setTimeout(() => {
        if (request.startRow === 0) {
          setRowData({ rows: result.rowData, rowCount: result.rowCount });
        } else {
          setRowData((prev) => {
            return { ...prev, rows: [...prev.rows, ...result.rowData] };
          });
        }
      }, 0);
      return {
        success: true,
        rowData: result.rowData,
      };
    },
  };
}

SFDataGrid.defaultProps = {
  allowMultipleDetailRows: true,
  persistState: true,
  loadAllRows: false,
  hasRowSelection: false,
  hasColSelection: false,
  rowSelection: "multiple",
  enableStatusBar: false,
  customStatusPanelsRight: [],
  enableResponseCaching: false,
  suppressRowClickSelection: true,
  hideGrid: false,
};

SFDataGrid.propTypes = {
  rowModelType: PropTypes.string,
  /* Api url to download data  */
  downloadUrl: PropTypes.string,
  /* Name of file data to download  */
  exportFileName: PropTypes.string,
  /* The API url from which grid requests row data  */
  datasourceUrl: PropTypes.string.isRequired,
  /* The API url from which grid requests values to show in filters  */
  filterValuesUrl: PropTypes.string.isRequired,
  gridColumnColors: PropTypes.objectOf(PropTypes.string),
  /* The API url from which grid requests values to show in filters  */
  viewDetailsUrl: PropTypes.string,
  /* >The API ulr from which grid request data for master-detail*/
  viewMasterDetailsUrl: PropTypes.string,
  /* Decides if grid should have a detail component  */
  masterDetail: PropTypes.bool,
  /* The name of the grid that is used to save values in the  redux slice  */
  gridName: PropTypes.string.isRequired,
  /* An array of columns definitions as objects  */
  columnDefs: PropTypes.arrayOf(
    PropTypes.shape({
      headerName: PropTypes.string,
      field: PropTypes.string,
    }).isRequired
  ).isRequired,
  /* Decides what component will be used when rendering details. Should be passed as a function */
  detailCellRenderer: PropTypes.func,
  globalFilters: PropTypes.shape({
    url: PropTypes.string,
    filters: PropTypes.arrayOf(
      PropTypes.shape({
        displayName: PropTypes.string,
        name: PropTypes.string,
        color: PropTypes.string,
      }).isRequired
    ),
  }),
  onHandleRow: PropTypes.func,
  gridState: PropTypes.number,
  /* Function when a row is added */
  canAdd: PropTypes.bool,
  /* Function when a row is edited */
  canEdit: PropTypes.bool,
  /* Function when a row is deleted */
  canDelete: PropTypes.bool,
  /* Function when a row is restored */
  canRestore: PropTypes.bool,
  /* Function when the grid view is toggled */
  onToggle: PropTypes.func,
  /* Sets the messages displayed in a toast when an action occurs */
  messages: PropTypes.shape({
    addSuccess: PropTypes.string,
    editSuccess: PropTypes.string,
    deleteSuccess: PropTypes.string,
    restoreSuccess: PropTypes.string,
  }),
  detailHeight: PropTypes.number,
  /* Decides whether multiple detail components can be open at the same time */
  allowMultipleDetailRows: PropTypes.bool,
  /* Decides if grid sorting and filtering state are saved */
  persistState: PropTypes.bool,
  /* Any data that needs to be passed in the detail component */
  detailProps: PropTypes.any,
  /* Decides whether all rows are loaded from start */
  loadAllRows: PropTypes.bool,
  /* Allow multiple row selection */
  rowSelection: PropTypes.oneOf(["single", "multiple"]),
  /* Enable row selection */
  hasRowSelection: PropTypes.bool,
  /* Enable columns selection */
  hasColSelection: PropTypes.bool,
  /* Any grid config regarding filter ,sorting and selection */
  gridConfig: PropTypes.shape({
    sortModel: PropTypes.array,
    rowGroupKey: PropTypes.string,
    filterModel: PropTypes.object,
    selection: PropTypes.shape({
      allSelected: PropTypes.bool,
      toggledNodes: PropTypes.arrayOf(PropTypes.string),
      selectedCount: PropTypes.number,
      unselectableRowCount: PropTypes.number,
    }),
    rowCount: PropTypes.number,
  }),
  columnGroupOptions: PropTypes.array,
  noCardBorder: PropTypes.bool,
  enableStatusBar: PropTypes.bool,
  customStatusPanelsRight: PropTypes.arrayOf(PropTypes.object),
  /*Flag that displays the filter tool panel button */
  disableFilterSideBar: PropTypes.bool,
  /*Flag that disables redux state for selection */
  disableSaveSelection: PropTypes.bool,
  /*Flag that enables caching responses from the API in redux */
  enableResponseCaching: PropTypes.bool,
  /*If timestamp for response cache object is older than this, it should refresh */
  cacheMinAge: PropTypes.string,
  /*Interval in minutes to force refresh response cache object */
  cacheRefreshInterval: PropTypes.number,
  /* If true (default), rows cannot be selected when clicked (checkbox selection still works) */
  suppressRowClickSelection: PropTypes.bool,
};

export default SFDataGrid;
